import { composerPlainText } from "../../../components/MessageHistory/composerHtmlUtils";

export const TEAM_MENTION_TODOS_LABEL = "todos";

export function isChatbotUser(u) {
  return Number(u?.ura) === 1;
}

/** Converte membros do espaço em listas para autocomplete e parse de @menções. */
export function membersToMentionCatalog(members = [], { excludeUserId = null } = {}) {
  const users = [];
  const agents = [];
  for (const m of members) {
    const pid = m?.participantId;
    if (m?.participantType === "user") {
      if (excludeUserId != null && String(pid) === String(excludeUserId)) continue;
      users.push({
        id: pid,
        nome: m.displayName || m.userName || m.nome || pid,
        ura: 0,
      });
    } else if (m?.participantType === "chatbot") {
      users.push({
        id: pid,
        nome: m.displayName || pid,
        ura: 1,
      });
    } else if (m?.participantType === "agent") {
      agents.push({
        id: pid,
        name: m.displayName || pid,
      });
    }
  }
  return { users, agents };
}

function mergeById(existing = [], incoming = [], { idKey = "id", labelKeys = ["nome", "name"] } = {}) {
  const map = new Map();
  for (const item of existing) {
    const id = String(item?.[idKey] ?? "").trim();
    if (!id) continue;
    map.set(id, item);
  }
  for (const item of incoming) {
    const id = String(item?.[idKey] ?? "").trim();
    if (!id) continue;
    const prev = map.get(id);
    if (!prev) {
      map.set(id, item);
      continue;
    }
    const hasLabel = labelKeys.some((k) => String(prev[k] || "").trim());
    if (!hasLabel) map.set(id, { ...prev, ...item });
  }
  return [...map.values()];
}

/** Membros do espaço + catálogo global (usuários, chatbots e agentes do tenant). */
export function mergeMentionCatalog(memberCatalog, globalUsers = [], globalAgents = []) {
  const fromMembers = memberCatalog || { users: [], agents: [] };
  return {
    users: mergeById(fromMembers.users, globalUsers, { idKey: "id", labelKeys: ["nome", "name"] }),
    agents: mergeById(fromMembers.agents, globalAgents, { idKey: "id", labelKeys: ["name", "nome"] }),
  };
}

/** Catálogo de menções: restrito aos membros (canais privados / DMs) ou membros + tenant. */
export function buildMentionCatalog({
  members = [],
  globalUsers = [],
  globalAgents = [],
  currentUserId = null,
  restrictToMembers = false,
} = {}) {
  const fromMembers = membersToMentionCatalog(members, { excludeUserId: currentUserId });
  const merged = restrictToMembers
    ? fromMembers
    : mergeMentionCatalog(fromMembers, globalUsers, globalAgents);
  const usersForPicker =
    currentUserId == null
      ? merged.users
      : merged.users.filter((u) => String(u.id) !== String(currentUserId));
  return {
    users: usersForPicker,
    agents: merged.agents,
    parseUsers: merged.users,
    parseAgents: merged.agents,
  };
}

/**
 * Localiza @ ativo na posição do cursor (permite menção no meio do texto).
 * @returns {{ atIndex: number, query: string } | null}
 */
export function findActiveMentionAt(text, cursorIndex) {
  const plain = composerPlainText(text, { trim: false });
  const len = plain.length;
  const cursor = Math.max(0, Math.min(Number.isFinite(Number(cursorIndex)) ? Number(cursorIndex) : len, len));
  const atIndex = plain.lastIndexOf("@", cursor - 1);
  if (atIndex < 0) return null;
  if (atIndex > 0 && !/\s/.test(plain[atIndex - 1])) return null;
  const query = plain.slice(atIndex + 1, cursor);
  if (/\s/.test(query)) return null;
  return { atIndex, query };
}

/** Canal de discussão interna vinculado a uma solicitação (slug solicitacao-{id}). */
export function isLeadDiscussionSpace(space) {
  if (!space || typeof space !== "object") return false;
  return /^solicitacao-\d+$/i.test(String(space.slug || ""));
}

/** Canal de discussão interna vinculado a uma tarefa (slug tarefa-{cardId}). */
export function isTaskDiscussionSpace(space) {
  if (!space || typeof space !== "object") return false;
  return /^tarefa-\d+$/i.test(String(space.slug || ""));
}

/** Discussão vinculada a solicitação ou tarefa (menções sem restringir a membros). */
export function isEntityDiscussionSpace(space) {
  return isLeadDiscussionSpace(space) || isTaskDiscussionSpace(space);
}

export function shouldRestrictMentionsToMembers(space) {
  if (!space || typeof space !== "object") return false;
  if (String(space.type || "").toLowerCase() === "dm") return true;
  if (isEntityDiscussionSpace(space)) return false;
  return Boolean(space.isPrivate);
}

/** IDs de usuários humanos que já participam do espaço. */
export function getSpaceMemberUserIds(members = []) {
  return new Set(
    (members || [])
      .filter((m) => m?.participantType === "user")
      .map((m) => String(m.participantId))
      .filter(Boolean)
  );
}

/** Opções do autocomplete @ (pessoas, chatbots, agentes e @todos). */
export function buildTeamMentionOptions({
  users = [],
  agents = [],
  query = "",
  labels = {},
  memberUserIds = null,
  showMemberJoinStatus = false,
} = {}) {
  const q = String(query || "").toLowerCase().trim();
  const options = [];
  const memberSet =
    memberUserIds instanceof Set ? memberUserIds : getSpaceMemberUserIds(memberUserIds || []);

  const todosLabel = TEAM_MENTION_TODOS_LABEL;
  if (!q || todosLabel.startsWith(q) || q === "tod") {
    options.push({
      type: "all",
      id: "todos",
      label: todosLabel,
      kind: labels.allUsers || "Todos",
    });
  }
  if (!q || "all".startsWith(q) || q === "al") {
    options.push({
      type: "all",
      id: "all",
      label: "all",
      kind: labels.allUsers || "Todos",
    });
  }

  const human = (users || [])
    .filter((u) => !isChatbotUser(u))
    .map((u) => {
      const option = {
        type: "user",
        id: u.id,
        label: String(u.nome || u.name || "").trim(),
        kind: labels.human || "Pessoa",
      };
      if (showMemberJoinStatus) {
        option.memberStatus = memberSet.has(String(u.id)) ? "member" : "willJoin";
      }
      return option;
    });

  const chatbots = (users || [])
    .filter(isChatbotUser)
    .map((u) => ({
      type: "chatbot",
      id: u.id,
      label: String(u.nome || u.name || "").trim(),
      kind: labels.chatbot || "Chatbot",
    }));

  const agentes = (agents || []).map((a) => ({
    type: "agent",
    id: a.id,
    label: String(a.name || a.nome || "").trim(),
    kind: labels.agent || "Agente de IA",
  }));

  const all = [...human, ...chatbots, ...agentes].filter((o) => o.label);
  let filtered = q
    ? all.filter((o) => o.label.toLowerCase().includes(q) || o.kind.toLowerCase().includes(q))
    : all;

  if (showMemberJoinStatus) {
    const statusRank = (o) => {
      if (o.memberStatus === "member") return 0;
      if (o.memberStatus === "willJoin") return 1;
      return 2;
    };
    filtered = [...filtered].sort((a, b) => {
      const byStatus = statusRank(a) - statusRank(b);
      if (byStatus !== 0) return byStatus;
      return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
    });
  }

  return [...options, ...filtered];
}

/** Extrai menções do texto (rótulos completos + @todos). */
export function parseTeamMentions(text, { users = [], agents = [] } = {}) {
  const mentions = { users: [], agents: [], chatbots: [], mentionAll: false };
  const plain = composerPlainText(text, { trim: true });
  if (!plain) return mentions;

  if (/@todos\b/i.test(plain) || /@all\b/i.test(plain)) {
    mentions.mentionAll = true;
  }

  const entries = [
    ...(users || []).map((u) => ({
      type: isChatbotUser(u) ? "chatbot" : "user",
      id: u.id,
      label: String(u.nome || u.name || "").trim(),
    })),
    ...(agents || []).map((a) => ({
      type: "agent",
      id: a.id,
      label: String(a.name || a.nome || "").trim(),
    })),
  ].filter((e) => e.label);

  entries.sort((a, b) => b.label.length - a.label.length);

  for (const entry of entries) {
    const escaped = entry.label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`@${escaped}(?=\\s|$|[.,!?;:])`, "i");
    if (!re.test(plain)) continue;
    if (entry.type === "user") {
      const uid = Number(entry.id);
      if (Number.isFinite(uid) && uid > 0 && !mentions.users.includes(uid)) {
        mentions.users.push(uid);
      }
    } else if (entry.type === "chatbot") {
      const cid = Number(entry.id);
      if (Number.isFinite(cid) && cid > 0 && !mentions.chatbots.includes(cid)) {
        mentions.chatbots.push(cid);
      }
    } else if (entry.type === "agent") {
      const aid = String(entry.id).trim();
      if (aid && !mentions.agents.includes(aid)) {
        mentions.agents.push(aid);
      }
    }
  }

  return mentions;
}

export function emptyTeamMentions() {
  return { users: [], agents: [], chatbots: [], mentionAll: false };
}

export function mergeTeamMentions(base, extra) {
  const a = base || emptyTeamMentions();
  const b = extra || emptyTeamMentions();
  return {
    users: [
      ...new Set(
        [...(a.users || []), ...(b.users || [])].map(Number).filter((n) => Number.isFinite(n) && n > 0)
      ),
    ],
    agents: [...new Set([...(a.agents || []), ...(b.agents || [])].map(String).filter(Boolean))],
    chatbots: [
      ...new Set(
        [...(a.chatbots || []), ...(b.chatbots || [])]
          .map(Number)
          .filter((n) => Number.isFinite(n) && n > 0)
      ),
    ],
    mentionAll: Boolean(a.mentionAll || b.mentionAll),
  };
}

function mentionLabelInPlain(plain, label) {
  const trimmed = String(label || "").trim();
  if (!trimmed) return false;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`@${escaped}(?=\\s|$|[.,!?;:])`, "i").test(plain);
}

/** Remove menções escolhidas no picker que não constam mais no texto. */
export function prunePickedMentionsToText(text, picked, { users = [], agents = [] } = {}) {
  const base = picked || emptyTeamMentions();
  const plain = composerPlainText(text, { trim: true });
  if (!plain) return emptyTeamMentions();

  const mentionAll =
    Boolean(base.mentionAll) && (/@todos\b/i.test(plain) || /@all\b/i.test(plain));

  const usersKept = (base.users || []).filter((id) => {
    const u = (users || []).find((x) => String(x.id) === String(id) && !isChatbotUser(x));
    const label = u ? String(u.nome || u.name || "").trim() : "";
    return label && mentionLabelInPlain(plain, label);
  });

  const chatbotsKept = (base.chatbots || []).filter((id) => {
    const u = (users || []).find((x) => String(x.id) === String(id) && isChatbotUser(x));
    const label = u ? String(u.nome || u.name || "").trim() : "";
    return label && mentionLabelInPlain(plain, label);
  });

  const agentsKept = (base.agents || []).filter((id) => {
    const a = (agents || []).find((x) => String(x.id) === String(id));
    const label = a ? String(a.name || a.nome || "").trim() : "";
    return label && mentionLabelInPlain(plain, label);
  });

  return {
    users: usersKept,
    agents: agentsKept,
    chatbots: chatbotsKept,
    mentionAll,
  };
}

/** Monta payload final: parse do texto + menções do picker ainda presentes no texto. */
export function buildComposerMentionsPayload(text, picked, { users = [], agents = [] } = {}) {
  const parsed = parseTeamMentions(text, { users, agents });
  const prunedPicked = prunePickedMentionsToText(text, picked, { users, agents });
  return mergeTeamMentions(parsed, prunedPicked);
}

/** Registra menção escolhida no picker (complementa o parse do texto). */
export function applyPickedMention(picked, option) {
  const next = {
    users: [...(picked?.users || [])],
    agents: [...(picked?.agents || [])],
    chatbots: [...(picked?.chatbots || [])],
    mentionAll: Boolean(picked?.mentionAll),
  };
  if (!option) return next;

  if (option.type === "all") {
    next.mentionAll = true;
    return next;
  }
  if (option.type === "agent") {
    const id = String(option.id).trim();
    if (id && !next.agents.includes(id)) next.agents.push(id);
    return next;
  }
  if (option.type === "chatbot") {
    const id = Number(option.id);
    if (Number.isFinite(id) && id > 0 && !next.chatbots.includes(id)) next.chatbots.push(id);
    return next;
  }
  const uid = Number(option.id);
  if (Number.isFinite(uid) && uid > 0 && !next.users.includes(uid)) next.users.push(uid);
  return next;
}
